import { useState, useEffect } from "react";
import ringsData from "../data/rings";
import pendantsData from "../data/pendants";
import AdminOrders from "./AdminOrders";
import styles from "./Admin.module.css";

const AUTH_API = "https://ratnapura-gems-api.vercel.app/api/auth";
const GEM_OPTIONS = ["Topaz", "Sapphire", "Tourmaline", "Garnet", "Citrine", "Amethyst", "Moonstone", "Spinel", "Iolite", "Peridot", "Pearl", "Opal"];

const EMPTY_FORM = {
  id: "", name: "", price: "", description: "", gem: "Topaz",
  category: "pendant", src: "", preorder: false, deal: false, originalPrice: "",
  details: { gemstone: "", carat: "", metal: "Sterling Silver (925)", size: "", origin: "Sri Lanka", certification: "Certified Authenticity" }
};

function generateJsFile(products, category) {
  const varName = category === "ring" ? "rings" : "pendants";
  const items = products
    .filter(p => p.category === category)
    .map(p => `  {
    id: "${p.id}",
    name: "${p.name}",
    price: ${p.price},${p.preorder ? "\n    preorder: true," : ""}${p.deal ? "\n    deal: true,\n    originalPrice: " + p.originalPrice + "," : ""}
    description: "${p.description}",
    gem: "${p.gem}",
    src: "${p.src}",
    details: { gemstone: "${p.details.gemstone}", carat: "${p.details.carat}", metal: "${p.details.metal}", size: "${p.details.size}", origin: "${p.details.origin}", certification: "${p.details.certification}" }
  }`)
    .join(",\n");
  return `const ${varName} = [\n${items}\n];\n\nexport default ${varName};\n`;
}

function downloadFile(content, filename) {
  const blob = new Blob([content], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    // Store the real password so the Orders tab can authenticate its
    // API calls after a page refresh.
    const saved = sessionStorage.getItem("rg_admin_pw");
    if (saved) {
      setPassword(saved);
      setAuthed(true);
      loadProducts();
    }
  }, []);

  const loadProducts = () => {
    setProducts([
      ...ringsData.map(r => ({ ...r, category: "ring" })),
      ...pendantsData.map(p => ({ ...p, category: "pendant" })),
    ]);
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [loggingIn, setLoggingIn] = useState(false);

  // Verify against the API rather than a hardcoded value, so the same
  // password also authorises the Orders tab's API calls.
  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const r = await fetch(AUTH_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${password}` },
      });
      if (r.status === 401) {
        setLoginError("Incorrect password");
      } else if (!r.ok) {
        setLoginError("Could not verify — please try again.");
      } else {
        sessionStorage.setItem("rg_admin_pw", password);
        setAuthed(true);
        loadProducts();
      }
    } catch {
      setLoginError("Could not reach the server. Check your connection.");
    }
    setLoggingIn(false);
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith("details.")) {
      const key = field.replace("details.", "");
      setForm(f => ({ ...f, details: { ...f.details, [key]: value } }));
    } else {
      setForm(f => ({ ...f, [field]: value }));
    }
  };

  const generateId = (category, name) => {
    const prefix = category === "ring" ? "ring" : "pendant";
    const slug = name.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 20);
    return `${prefix}-${slug}-${Date.now().toString().slice(-4)}`;
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.src) {
      showToast("Name, price and image path are required", "error");
      return;
    }
    const updated = editing
      ? products.map(p => p.id === form.id ? { ...form, price: parseInt(form.price), originalPrice: form.originalPrice ? parseInt(form.originalPrice) : undefined } : p)
      : [...products, { ...form, price: parseInt(form.price), originalPrice: form.originalPrice ? parseInt(form.originalPrice) : undefined, id: generateId(form.category, form.name) }];

    setProducts(updated);
    setHasChanges(true);
    showToast(editing ? "Product updated! Download files to apply." : "Product added! Download files to apply.");
    setForm(EMPTY_FORM);
    setEditing(false);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    setForm({ ...product, price: String(product.price), details: product.details || EMPTY_FORM.details });
    setEditing(true);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this product?")) return;
    setProducts(prev => prev.filter(p => p.id !== id));
    setHasChanges(true);
    showToast("Product deleted! Download files to apply.");
  };

  const handleDownload = (category) => {
    const content = generateJsFile(products, category);
    const filename = category === "ring" ? "rings.js" : "pendants.js";
    downloadFile(content, filename);
    showToast(`${filename} downloaded! Replace the file in src/data/ and rebuild.`);
  };

  const handleDownloadBoth = () => {
    handleDownload("ring");
    setTimeout(() => handleDownload("pendant"), 500);
  };

  const filtered = products.filter(p => {
    const matchCat = filterCategory === "all" || p.category === filterCategory;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (!authed) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <div className={styles.loginLogo}>Ratnapura <span>Gems</span></div>
          <h2 className={styles.loginTitle}>Admin Dashboard</h2>
          <form onSubmit={login} className={styles.loginForm}>
            <input type="password" placeholder="Enter admin password" value={password}
              onChange={e => { setPassword(e.target.value); setLoginError(""); }}
              className={styles.loginInput} autoFocus />
            {loginError && <p className={styles.loginError}>{loginError}</p>}
            <button type="submit" className={styles.loginBtn} disabled={loggingIn}>
              {loggingIn ? "Verifying…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {toast && <div className={`${styles.toast} ${toast.type === "error" ? styles.toastError : styles.toastSuccess}`}>{toast.msg}</div>}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerLogo}>Ratnapura <span>Gems</span></div>
          <span className={styles.headerBadge}>Admin</span>
        </div>
        <div className={styles.headerRight}>
          <a href="/" className={styles.viewSiteBtn}>View Site ↗</a>
          <button className={styles.logoutBtn} onClick={() => { sessionStorage.removeItem("rg_admin_pw"); setPassword(""); setAuthed(false); }}>Logout</button>
        </div>
      </div>

      <div className={styles.content}>

        {/* Tabs */}
        <div className={styles.adminTabs}>
          <button
            className={`${styles.adminTab} ${tab === "products" ? styles.adminTabActive : ""}`}
            onClick={() => setTab("products")}
          >Products</button>
          <button
            className={`${styles.adminTab} ${tab === "orders" ? styles.adminTabActive : ""}`}
            onClick={() => setTab("orders")}
          >Order Tracking</button>
        </div>

        {tab === "orders" && (
          <AdminOrders password={password} showToast={showToast} />
        )}

        {tab === "products" && <>

        {/* Download banner */}
        {hasChanges && (
          <div className={styles.downloadBanner}>
            <div>
              <p className={styles.downloadBannerTitle}>⚠️ You have unsaved changes</p>
              <p className={styles.downloadBannerText}>Download the updated files, replace them in <code>src/data/</code>, then run <code>npm run build</code> to publish.</p>
            </div>
            <button className={styles.downloadBothBtn} onClick={handleDownloadBoth}>
              ⬇ Download Both Files
            </button>
          </div>
        )}

        {/* Stats */}
        <div className={styles.stats}>
          {[
            { num: products.length, label: "Total Products" },
            { num: products.filter(p => p.category === "ring").length, label: "Rings" },
            { num: products.filter(p => p.category === "pendant").length, label: "Pendants" },
            { num: products.filter(p => p.preorder).length, label: "Pre-order" },
          ].map(s => (
            <div key={s.label} className={styles.statCard}>
              <div className={styles.statNum}>{s.num}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Download buttons */}
        <div className={styles.downloadRow}>
          <p className={styles.downloadHint}>After making changes, download the updated files and replace them in your project:</p>
          <div className={styles.downloadBtns}>
            <button className={styles.downloadBtn} onClick={() => handleDownload("pendant")}>⬇ Download pendants.js</button>
            <button className={styles.downloadBtn} onClick={() => handleDownload("ring")}>⬇ Download rings.js</button>
          </div>
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <input type="text" placeholder="Search products..." value={search}
              onChange={e => setSearch(e.target.value)} className={styles.searchInput} />
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className={styles.filterSelect}>
              <option value="all">All</option>
              <option value="ring">Rings</option>
              <option value="pendant">Pendants</option>
            </select>
          </div>
          <button className={styles.addBtn} onClick={() => { setForm(EMPTY_FORM); setEditing(false); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            + Add Product
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className={styles.formCard}>
            <h2 className={styles.formTitle}>{editing ? "Edit Product" : "Add New Product"}</h2>
            <form onSubmit={handleSave} className={styles.form}>
              <div className={styles.formGrid}>
                <div className={styles.formCol}>
                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Basic Info</h3>
                    <div className={styles.field}>
                      <label>Product Name *</label>
                      <input value={form.name} onChange={e => handleFormChange("name", e.target.value)} placeholder="Blue Topaz Pendant" required />
                    </div>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}>
                        <label>Category *</label>
                        <select value={form.category} onChange={e => handleFormChange("category", e.target.value)}>
                          <option value="pendant">Pendant</option>
                          <option value="ring">Ring</option>
                        </select>
                      </div>
                      <div className={styles.field}>
                        <label>Gem Type</label>
                        <select value={form.gem} onChange={e => handleFormChange("gem", e.target.value)}>
                          {GEM_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}>
                        <label>Price (LKR) *</label>
                        <input type="number" value={form.price} onChange={e => handleFormChange("price", e.target.value)} placeholder="20000" required />
                      </div>
                      <div className={styles.field}>
                        <label>Pre-order</label>
                        <div className={styles.toggleRow}>
                          <label className={styles.toggle}>
                            <input type="checkbox" checked={form.preorder} onChange={e => handleFormChange("preorder", e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span>{form.preorder ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}>
                        <label>Deal / Sale</label>
                        <div className={styles.toggleRow}>
                          <label className={styles.toggle}>
                            <input type="checkbox" checked={form.deal} onChange={e => handleFormChange("deal", e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span>{form.deal ? "Yes" : "No"}</span>
                        </div>
                      </div>
                      {form.deal && (
                        <div className={styles.field}>
                          <label>Original Price (LKR)</label>
                          <input type="number" value={form.originalPrice} onChange={e => handleFormChange("originalPrice", e.target.value)} placeholder="25000" />
                        </div>
                      )}
                    </div>
                    <div className={styles.field}>
                      <label>Description</label>
                      <textarea value={form.description} onChange={e => handleFormChange("description", e.target.value)} rows={3} />
                    </div>
                    <div className={styles.field}>
                      <label>Image Path * <span style={{fontWeight:400,color:"#7a6e68"}}>(e.g. /pendants/RGP-001.webp)</span></label>
                      <input value={form.src} onChange={e => handleFormChange("src", e.target.value)} placeholder="/pendants/RGP-001.webp" required />
                    </div>
                  </div>

                  <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Gem Details</h3>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}><label>Gemstone</label><input value={form.details.gemstone} onChange={e => handleFormChange("details.gemstone", e.target.value)} placeholder="Blue Topaz" /></div>
                      <div className={styles.field}><label>Carat</label><input value={form.details.carat} onChange={e => handleFormChange("details.carat", e.target.value)} placeholder="1.30 ct" /></div>
                    </div>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}><label>Metal</label><input value={form.details.metal} onChange={e => handleFormChange("details.metal", e.target.value)} /></div>
                      <div className={styles.field}><label>Size</label><input value={form.details.size} onChange={e => handleFormChange("details.size", e.target.value)} placeholder="18mm x 10mm" /></div>
                    </div>
                    <div className={styles.fieldRow}>
                      <div className={styles.field}><label>Origin</label><input value={form.details.origin} onChange={e => handleFormChange("details.origin", e.target.value)} /></div>
                      <div className={styles.field}><label>Certification</label><input value={form.details.certification} onChange={e => handleFormChange("details.certification", e.target.value)} /></div>
                    </div>
                  </div>
                </div>

                <div className={styles.formCol}>
                  {form.src && (
                    <div className={styles.formSection}>
                      <h3 className={styles.sectionTitle}>Preview</h3>
                      <img src={form.src} alt="Preview" className={styles.imagePreview} />
                      <div className={styles.previewCard}>
                        <div className={styles.previewInfo}>
                          <p className={styles.previewName}>{form.name}</p>
                          <p className={styles.previewGem}>{form.details.gemstone} · {form.details.carat}</p>
                          <p className={styles.previewPrice}>LKR {parseInt(form.price || 0).toLocaleString()}</p>
                          {form.preorder && <span className={styles.previewPreorder}>Pre-order</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => { setForm(EMPTY_FORM); setEditing(false); setShowForm(false); }}>Cancel</button>
                <button type="submit" className={styles.saveBtn}>{editing ? "Update Product" : "Add Product"}</button>
              </div>
            </form>
          </div>
        )}

        {/* Products Table */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>Products <span className={styles.tableCount}>{filtered.length}</span></h2>
          </div>
          <div className={styles.table}>
            <div className={styles.tableHead}>
              <span>Product</span><span>Category</span><span>Price</span><span>Status</span><span>Actions</span>
            </div>
            {filtered.map(product => (
              <div key={product.id} className={styles.tableRow}>
                <div className={styles.tableProduct}>
                  <img src={product.src} alt={product.name} className={styles.tableThumb} />
                  <div>
                    <p className={styles.tableProductName}>{product.name}</p>
                    <p className={styles.tableProductGem}>{product.gem} · {product.details?.carat}</p>
                  </div>
                </div>
                <span className={`${styles.tableCategoryBadge} ${styles[product.category]}`}>{product.category}</span>
                <span className={styles.tablePrice}>LKR {product.price?.toLocaleString()}</span>
                <span className={`${styles.tableStatus} ${product.preorder ? styles.preorderStatus : styles.availableStatus}`}>
                  {product.preorder ? "Pre-order" : "Available"}
                </span>
                <div className={styles.tableActions}>
                  <button className={styles.editBtn} onClick={() => handleEdit(product)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={() => handleDelete(product.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        </>}

      </div>
    </div>
  );
}